import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import hrService from '../../../services/hrService';

const EmploymentHistoryTab = ({ employee }) => {
  const [employmentHistory, setEmploymentHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (employee?.id) {
      // CRITICAL FIX: Add detailed console logging to help diagnose missing fields
      console.log('EmploymentHistoryTab received employee data:', {
        id: employee?.id,
        name: employee?.name,
        email: employee?.email,
        username: employee?.username,
        dateOfJoining: employee?.dateOfJoining,
        joinDate: employee?.joinDate,
        branchName: employee?.branchName,
        reportingManager: employee?.reportingManager,
        personalDetails: employee?.personalDetails
      });
      
      loadEmploymentHistory();
    }
  }, [employee?.id]);

  const loadEmploymentHistory = async () => {
    setLoading(true);
    setError('');
    try {
      // For demonstration, we'll show related user history
      // In a real implementation, you would have an employment_history table
      const history = await getRelatedUserHistory(employee?.id);
      setEmploymentHistory(history);
    } catch (err) {
      console.error('Error loading employment history:', err);
      setError('Failed to load employment history');
      setEmploymentHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const getRelatedUserHistory = async (employeeId) => {
    try {
      // Get employee's related records from various tables
      const [leaveRequests, attendanceRecords, salaryRecords] = await Promise.allSettled([
        hrService?.getLeaveRequests({ employeeId }),
        hrService?.getAttendanceRecords({ employeeId, limit: 10 }),
        hrService?.getPayrollRecords({ employeeId })
      ]);

      const history = [];

      // Add leave requests to history
      if (leaveRequests?.status === 'fulfilled' && leaveRequests?.value) {
        leaveRequests?.value?.forEach(leave => {
          history?.push({
            id: `leave-${leave?.id}`,
            type: 'Leave Request',
            date: leave?.createdAt,
            description: `${leave?.leaveType} leave from ${leave?.startDate} to ${leave?.endDate}`,
            status: leave?.leaveStatus,
            details: {
              reason: leave?.reason,
              totalDays: leave?.totalDays,
              approvedBy: leave?.approvedByProfile?.fullName
            }
          });
        });
      }

      // Add attendance records to history
      if (attendanceRecords?.status === 'fulfilled' && attendanceRecords?.value) {
        attendanceRecords?.value?.slice(0, 5)?.forEach(attendance => {
          history?.push({
            id: `attendance-${attendance?.id}`,
            type: 'Attendance',
            date: attendance?.attendanceDate,
            description: `${attendance?.attendanceStatus} - ${attendance?.checkInTime || 'N/A'} to ${attendance?.checkOutTime || 'N/A'}`,
            status: attendance?.attendanceStatus,
            details: {
              totalHours: attendance?.totalHours,
              notes: attendance?.notes
            }
          });
        });
      }

      // Add salary records to history
      if (salaryRecords?.status === 'fulfilled' && salaryRecords?.value) {
        salaryRecords?.value?.slice(0, 3)?.forEach(salary => {
          history?.push({
            id: `salary-${salary?.id}`,
            type: 'Payroll',
            date: salary?.processedAt || salary?.createdAt,
            description: `Salary processed for ${salary?.payPeriodStart} to ${salary?.payPeriodEnd}`,
            status: salary?.payrollStatus || 'processed',
            details: {
              baseSalary: salary?.baseSalary,
              grossPay: salary?.grossPay,
              netPay: salary?.netPay
            }
          });
        });
      }

      // Sort by date (newest first)
      history?.sort((a, b) => new Date(b?.date) - new Date(a?.date));

      return history;
    } catch (error) {
      console.error('Error fetching related user history:', error);
      return [];
    }
  };

  // Get employment-related fields from employee data
  const {
    username = '',
    branchName = '',
    reportingManager = '',
    designation = '',
    joinDate = '',
    dateOfJoining = '',
    position = '',
    department = '',
    email = '',
    employmentStatus = 'active',
    workShift = 'day',
    roleAssigned = '',
    personalDetails = {}
  } = employee || {};

  // CRITICAL FIX: Add console logging for extracted values to help diagnose
  useEffect(() => {
    if (employee?.id) {
      console.log('EmploymentHistoryTab extracted values:', {
        username: username || 'MISSING',
        email: email || 'MISSING',
        dateOfJoining: dateOfJoining || joinDate || 'MISSING',
        branchName: branchName || 'MISSING',
        reportingManager: reportingManager || 'MISSING'
      });
    }
  }, [employee, username, email, dateOfJoining, joinDate, branchName, reportingManager]);

  // CRITICAL FIX: Multiple fallback extraction for location fields
  // Support both nested structure AND direct top-level properties
  const location = personalDetails?.address?.street || employee?.location || '';
  const city = personalDetails?.address?.city || employee?.city || '';
  const state = personalDetails?.address?.state || employee?.state || '';
  const zipCode = personalDetails?.address?.zipCode || employee?.zipCode || employee?.zip || '';
  const emergencyContactEmail = personalDetails?.emergencyContact?.email || 
                                personalDetails?.emergencyContactEmail || 
                                employee?.emergencyContactEmail || '';

  // Format the date to be more readable
  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    try {
      const date = new Date(dateString);
      return date?.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  };

  // Status badge color based on employment status
  const getStatusColor = (status) => {
    const statusColors = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-gray-100 text-gray-800',
      terminated: 'bg-red-100 text-red-800',
      on_leave: 'bg-yellow-100 text-yellow-800',
      resigned: 'bg-orange-100 text-orange-800'
    };
    return statusColors?.[status] || 'bg-gray-100 text-gray-800';
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'Leave Request':
        return 'Calendar';
      case 'Attendance':
        return 'Clock';
      case 'Payroll':
        return 'DollarSign';
      default:
        return 'FileText';
    }
  };

  if (!employee?.id) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Icon name="User" size={48} color="var(--color-muted)" />
        <h3 className="mt-4 text-lg font-medium text-gray-900">No Employee Selected</h3>
        <p className="mt-2 text-sm text-gray-500">
          Select an employee to view their employment history.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon name="Loader2" size={24} className="animate-spin mr-2" />
        <span className="text-gray-600">Loading employment history...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Icon name="AlertCircle" size={48} color="var(--color-destructive)" />
        <h3 className="mt-4 text-lg font-medium text-gray-900">Error Loading History</h3>
        <p className="mt-2 text-sm text-gray-500">{error}</p>
        <button
          onClick={loadEmploymentHistory}
          className="mt-4 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  if (employmentHistory?.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Icon name="FileText" size={48} color="var(--color-muted)" />
        <h3 className="mt-4 text-lg font-medium text-gray-900">No Employment History</h3>
        <p className="mt-2 text-sm text-gray-500">
          This is a new employee with no history records yet.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ENHANCED: Add helpful diagnostic message when fields are missing */}
      {(!username || !email || (!dateOfJoining && !joinDate)) && (
        <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-center gap-2">
            <Icon name="AlertCircle" size={16} color="#f59e0b" />
            <p className="text-sm font-medium text-yellow-800">
              Some employment details are not available for this employee. Missing fields:
              {!username && ' Username'}
              {!email && ' Email'}
              {(!dateOfJoining && !joinDate) && ' Date of Joining'}
            </p>
          </div>
          <p className="text-xs text-yellow-700 mt-1">
            These fields need to be added in the database or when editing the employee profile.
          </p>
        </div>
      )}
      
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4">Current Employment Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-4 bg-muted/30 rounded-lg border border-border">
          {/* ENHANCED: Add visual indicator for missing required fields */}
          <div className={!username ? 'opacity-50' : ''}>
            <label className="text-sm font-medium text-muted-foreground flex items-center gap-1">
              Username
              {!username && <Icon name="AlertCircle" size={12} color="#f59e0b" />}
            </label>
            <p className="mt-1 text-foreground font-medium">
              {username || <span className="text-yellow-600 italic">Not set - update required</span>}
            </p>
          </div>

          <div className={!email ? 'opacity-50' : ''}>
            <label className="text-sm font-medium text-muted-foreground flex items-center gap-1">
              Email
              {!email && <Icon name="AlertCircle" size={12} color="#f59e0b" />}
            </label>
            <p className="mt-1 text-foreground font-medium">
              {email || <span className="text-yellow-600 italic">Not set - update required</span>}
            </p>
          </div>

          <div>
            <label className="text-sm font-medium text-muted-foreground">Branch Name</label>
            <p className="mt-1 text-foreground font-medium">{branchName || 'Not set'}</p>
          </div>

          <div>
            <label className="text-sm font-medium text-muted-foreground">Reporting Manager</label>
            <p className="mt-1 text-foreground font-medium">{reportingManager || 'Not set'}</p>
          </div>

          <div className={(!dateOfJoining && !joinDate) ? 'opacity-50' : ''}>
            <label className="text-sm font-medium text-muted-foreground flex items-center gap-1">
              Date of Joining
              {(!dateOfJoining && !joinDate) && <Icon name="AlertCircle" size={12} color="#f59e0b" />}
            </label>
            <p className="mt-1 text-foreground font-medium">
              {formatDate(dateOfJoining || joinDate) === 'Not set' ? 
                <span className="text-yellow-600 italic">Not set - update required</span> : 
                formatDate(dateOfJoining || joinDate)
              }
            </p>
          </div>

          <div>
            <label className="text-sm font-medium text-muted-foreground">Location</label>
            <p className="mt-1 text-foreground font-medium">{location || 'Not set'}</p>
          </div>

          <div>
            <label className="text-sm font-medium text-muted-foreground">City</label>
            <p className="mt-1 text-foreground font-medium">{city || 'Not set'}</p>
          </div>

          <div>
            <label className="text-sm font-medium text-muted-foreground">State</label>
            <p className="mt-1 text-foreground font-medium">{state || 'Not set'}</p>
          </div>

          <div>
            <label className="text-sm font-medium text-muted-foreground">Zip Code</label>
            <p className="mt-1 text-foreground font-medium">{zipCode || 'Not set'}</p>
          </div>

          <div>
            <label className="text-sm font-medium text-muted-foreground">Position</label>
            <p className="mt-1 text-foreground font-medium">{position || 'Not set'}</p>
          </div>

          <div>
            <label className="text-sm font-medium text-muted-foreground">Department</label>
            <p className="mt-1 text-foreground font-medium">{department || 'Not set'}</p>
          </div>

          <div>
            <label className="text-sm font-medium text-muted-foreground">Designation</label>
            <p className="mt-1 text-foreground font-medium">{designation || 'Not set'}</p>
          </div>

          <div>
            <label className="text-sm font-medium text-muted-foreground">Role Assigned</label>
            <p className="mt-1 text-foreground font-medium">{roleAssigned || 'Not set'}</p>
          </div>

          <div>
            <label className="text-sm font-medium text-muted-foreground">Work Shift</label>
            <p className="mt-1 text-foreground font-medium capitalize">{workShift?.replace('_', ' ') || 'Day'}</p>
          </div>

          <div>
            <label className="text-sm font-medium text-muted-foreground">Employment Status</label>
            <span className={`inline-flex items-center mt-1 px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(employmentStatus)}`}>
              {employmentStatus?.replace('_', ' ')?.toUpperCase()}
            </span>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between border-t border-border pt-6">
        <h3 className="text-lg font-medium text-gray-900">
          Employment History for {employee?.name || employee?.userProfiles?.fullName}
        </h3>
        <button
          onClick={loadEmploymentHistory}
          className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
          title="Refresh history"
        >
          <Icon name="RefreshCw" size={20} />
        </button>
      </div>

      <div className="space-y-4">
        {employmentHistory?.map((record) => (
          <div
            key={record?.id}
            className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <Icon 
                    name={getTypeIcon(record?.type)} 
                    size={20} 
                    color="rgb(59, 130, 246)" 
                  />
                </div>
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <h4 className="text-sm font-medium text-gray-900">
                      {record?.type}
                    </h4>
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(record?.status)}`}>
                      {record?.status}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">
                    {record?.description}
                  </p>
                  {record?.details && (
                    <div className="text-xs text-gray-500 space-y-1">
                      {record?.type === 'Leave Request' && (
                        <>
                          {record?.details?.reason && (
                            <div><strong>Reason:</strong> {record?.details?.reason}</div>
                          )}
                          {record?.details?.totalDays && (
                            <div><strong>Duration:</strong> {record?.details?.totalDays} days</div>
                          )}
                          {record?.details?.approvedBy && (
                            <div><strong>Approved by:</strong> {record?.details?.approvedBy}</div>
                          )}
                        </>
                      )}
                      {record?.type === 'Attendance' && (
                        <>
                          {record?.details?.totalHours && (
                            <div><strong>Total Hours:</strong> {record?.details?.totalHours}</div>
                          )}
                          {record?.details?.notes && (
                            <div><strong>Notes:</strong> {record?.details?.notes}</div>
                          )}
                        </>
                      )}
                      {record?.type === 'Payroll' && (
                        <>
                          {record?.details?.baseSalary && (
                            <div><strong>Base Salary:</strong> ₹{record?.details?.baseSalary?.toLocaleString()}</div>
                          )}
                          {record?.details?.netPay && (
                            <div><strong>Net Pay:</strong> ₹{record?.details?.netPay?.toLocaleString()}</div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="text-xs text-gray-500">
                {formatDate(record?.date)}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default EmploymentHistoryTab;